import * as Sentry from '@sentry/react';
import { Container } from 'react-bootstrap';
import { BrowserRouter, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { CookieConsentProvider } from './contexts/CookieConsentContext';
import CookieConsentBanner from './components/shared/CookieConsentBanner';
import GA4Manager from './components/shared/GA4Manager';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import CategoriesPage from './pages/CategoriesPage';
import SearchSection from './components/SearchSection';
import CategoryPage from './pages/CategoryPage';
import JobDetail from './components/jobs/JobDetail';
import BrowseJobs from './components/jobs/BrowseJobs';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import VerifyEmailPending from './components/auth/VerifyEmailPending';
import VerifyEmailConfirm from './components/auth/VerifyEmailConfirm';
import CandidateDashboard from './components/candidate/CandidateDashboard';
import CompanyDashboard from './components/company/CompanyDashboard';
import CompanyBilling from './components/company/dashboard/CompanyBilling';
import CompanyOnboarding from './components/company/CompanyOnboarding';
import PostJobWizard from './components/company/PostJobWizard';
import CompanyOnboardingRouteGuard from './components/company/CompanyOnboardingRouteGuard';
import ForCompanies from './components/pages/ForCompanies';
import PaymentSuccess from './components/pages/PaymentSuccess';
import PrivacyPolicy from './components/pages/PrivacyPolicy';
import TermsOfService from './components/pages/TermsOfService';
import CookiePolicy from './components/pages/CookiePolicy';
import Unsubscribe from './components/pages/Unsubscribe';
import ContactPage from './components/pages/ContactPage';
import InviteAcceptPage from './components/pages/InviteAcceptPage';
import AdminPanel from './components/admin/AdminPanel';
import TwoFactorSetup from './components/admin/TwoFactorSetup';
import GoogleCallback from './components/auth/GoogleCallback';
import CompanyProfile from './components/pages/CompanyProfile';

// Initialize Sentry — only when DSN is configured (production)
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_ENV || 'production',
    tracesSampleRate: 0.1,
    // GDPR: do not send PII
    beforeSend(event) {
      // Strip user email from events
      if (event.user) {
        delete event.user.email;
        delete event.user.ip_address;
      }
      return event;
    },
  });
}

function HomeSearchSection() {
  const { pathname } = useLocation();
  if (pathname !== '/') {
    return null;
  }
  return <SearchSection />;
}

function MainWithContainer() {
  return (
    <Container>
      <main className="py-5">
        <Outlet />
      </main>
    </Container>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CookieConsentProvider>
          <CookieConsentBanner />
          <GA4Manager />
        <div className="rwm-app-layout">
          <Header />
          <div className="rwm-app-layout__body">
            <HomeSearchSection />
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/admin/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/auth/google/callback" element={<GoogleCallback />} />
              <Route path="/verify-email-pending" element={<VerifyEmailPending />} />
              <Route path="/verify-email/:id/:hash" element={<VerifyEmailConfirm />} />
              <Route path="/dashboard" element={<CandidateDashboard />} />
              <Route path="/company/onboarding" element={<CompanyOnboarding />} />
              <Route
                path="/company/dashboard"
                element={
                  <CompanyOnboardingRouteGuard>
                    <CompanyDashboard />
                  </CompanyOnboardingRouteGuard>
                }
              />
              <Route
                path="/company/post-job"
                element={
                  <CompanyOnboardingRouteGuard>
                    <PostJobWizard />
                  </CompanyOnboardingRouteGuard>
                }
              />
              <Route path="/company/billing" element={<CompanyBilling />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/admin" element={<AdminPanel />} />
              <Route path="/admin/2fa/setup" element={<TwoFactorSetup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/invite/accept" element={<InviteAcceptPage />} />
              <Route path="/jobs/:slug" element={<JobDetail />} />
              <Route path="/jobs" element={<BrowseJobs />} />
              <Route path="/companies/:slug" element={<CompanyProfile />} />
              <Route path="/for-companies" element={<ForCompanies />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route element={<MainWithContainer />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/categories" element={<CategoriesPage />} />
                <Route path="/job-by-category/:slug" element={<CategoryPage />} />
              </Route>
            </Routes>
          </div>
          <Footer />
        </div>
        </CookieConsentProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
